import React, { useState, useEffect, useRef, useCallback } from "react";
import { Icon } from "@iconify/react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import FileModalServiceAddr from "../Modal/FileModalServiceAddr";
import SearchableSelect from "../../components/SearchableSelect";
import ContactTable from "../../components/Table/TableContract";
import ContactModal from "../../components/Modal/ContactModal";
import { getData, insertData } from "../../../utils/SamuiUtils";

const FlyToNewPosition = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return;
    map.flyTo([parseFloat(lat), parseFloat(lng)], map.getZoom(), { animate: true, duration: 0.4 });
  }, [lat, lng, map]);
  return null;
};


const ClickToMoveMarker = ({ onMove }) => {
  useMapEvents({
    click: (e) => onMove(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

export default function ServiceLocationModal({
  isOpen,
  onClose,
  onSave,
  location,
  ServiceAddr_Id,
  serviceTypes = [],
  onReload,
  viewMode,
  AR_Id,
  fromCustomerPage = false,
  defaultServicePurpose = null, // prop สำหรับตั้งค่า ServicePurpose default
}) {
  const init = {
    ServiceAddr_Type_Id: "",
    AddrNo: "-",
    Building: "",
    Village: "",
    Moo: "",
    Soi: "",
    Road: "",
    Province: "",
    District: "",
    SubDistrict: "",
    Zipcode: "",
    AddrRemark: "",
    Latitude: 7.8804,
    Longitude: 98.3923,
    Width: "",
    Length: "",
    Height: "",
    SpecRemark: "",
    Appoint_Remark: "",
    Advisor_Name: "",
    Advisor_Tel: "",
    Sale_Emp_Id: "",
    Comp_Id: 1,
  };

  const [formData, setFormData] = useState(init);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [pendingContacts, setPendingContacts] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showFileModal, setShowFileModal] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const [mapType, setMapType] = useState("hybrid"); 
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const locationRef = useRef(null); // ใช้ track location เก่า
  const [initialServicePurpose, setInitialServicePurpose] = useState(null); // เก็บ ServicePurpose เริ่มต้น
  const [serviceTypeOptions, setServiceTypeOptions] = useState(serviceTypes);
  const [servicePurposeOptions, setServicePurposeOptions] = useState([]);
  const normalizeServicePurposes = (payload) => {
    const rows = Array.isArray(payload) ? payload : payload?.data || [];
    return rows
      .map((item) => ({
        id:
          item?.ServicePurpose_Id ??
          item?.ServicePurposeId ??
          item?.Purpose_Id ??
          item?.id ??
          item?.Id,
        name:
          item?.ServicePurpose_Name ??
          item?.Purpose_Name ??
          item?.name ??
          item?.ServicePurpose,
        status: item?.ServicePurpose_Status ?? item?.status ?? "Y",
      }))
      .filter((item) => item.id && item.name);
  };


  useEffect(() => {
    setServiceTypeOptions(serviceTypes);
  }, [serviceTypes]);

  const normalizeServiceTypes = (payload) => {
    const rows = Array.isArray(payload) ? payload : payload?.data || [];
    return rows
      .map((item) => ({
        id:
          item?.ServiceAddr_Type_Id ??
          item?.ServiceAddrType_Id ??
          item?.id ??
          item?.Id,
        name:
          item?.ServiceAddr_Type_Name ??
          item?.ServiceAddrType_Name ??
          item?.name ??
          item?.ServiceAddr_Type,
        status: item?.ServiceAddr_Type_Status ?? item?.status ?? "Y",
      }))
      .filter((item) => item.id && item.name);
  };

  const refreshServiceTypes = async () => {
    try {
      const res = await getData(
        "Tb_Set_ServiceAddrType",
        "ServiceAddr_Type_Status = 'Y'"
      );
      const normalized = normalizeServiceTypes(res);
      if (normalized.length > 0) {
        setServiceTypeOptions(normalized);
      }
      return normalized;
    } catch (err) {
      console.error("โหลดประเภทสถานที่บริการไม่สำเร็จ:", err);
      return [];
    }
  };

  const refreshServicePurposes = useCallback(async () => {
    try {
      const res = await getData(
        "Tb_Set_ServicePurpose",
        "ServicePurpose_Status = 'Y'"
      );
      const normalized = normalizeServicePurposes(res);
      if (normalized.length > 0) {
        setServicePurposeOptions(normalized);
        return normalized;
      }
    } catch (err) {
      console.error("โหลดวัตถุประสงค์ไม่สำเร็จ:", err);
    }

    setServicePurposeOptions([]);
    return [];
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    refreshServicePurposes();
  }, [isOpen, refreshServicePurposes]);

  // โหลด master ประเภทสถานที่บริการเมื่อเปิด modal
  useEffect(() => {
    if (!isOpen) return;
    refreshServiceTypes();
  }, [isOpen]);

  const handleAddServiceType = async () => {
    const { value, isConfirmed } = await Swal.fire({
      title: "เพิ่มประเภทสถานที่บริการ",
      input: "text",
      inputPlaceholder: "กรอกชื่อประเภทสถานที่บริการ",
      confirmButtonText: "บันทึก",
      cancelButtonText: "ยกเลิก",
      showCancelButton: true,
      buttonsStyling: true,
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
        input: "swal2-input-rounded",
      },
      willOpen: () => {
        const swalContainer = document.querySelector(".swal2-container");
        if (swalContainer) {
          swalContainer.style.zIndex = "20000";
        }
        const swalBackdrop = document.querySelector(".swal2-backdrop-show");
        if (swalBackdrop) {
          swalBackdrop.style.zIndex = "19999";
        }
      },
    });

    if (!isConfirmed) return;

    const name = String(value || "").trim();
    if (!name) {
      Swal.fire("กรอกข้อมูลไม่ครบ", "กรุณากรอกชื่อประเภทสถานที่บริการ", "warning");
      return;
    }

    const result = await insertData(
      "Tb_Set_ServiceAddrType",
      "ServiceAddr_Type_Name, ServiceAddr_Type_Status",
      [name, "Y"]
    );

    if (result?.status === "OK") {
      const updated = await refreshServiceTypes();
      const newRow = Array.isArray(result?.data) ? result.data[0] : null;
      const newId = newRow?.ServiceAddr_Type_Id || newRow?.id;
      if (newId) {
        setFormData((prev) => ({
          ...prev,
          ServiceAddr_Type_Id: String(newId),
        }));
      } else if (updated.length > 0) {
        const latest = updated[updated.length - 1];
        setFormData((prev) => ({
          ...prev,
          ServiceAddr_Type_Id: String(latest.id),
        }));
      }
      Swal.fire("สำเร็จ", "เพิ่มประเภทสถานที่บริการแล้ว", "success");
    } else {
      Swal.fire("ผิดพลาด", result?.message || "เพิ่มประเภทสถานที่บริการไม่สำเร็จ", "error");
    }
  };

  const handleAddServicePurpose = async () => {
    const { value, isConfirmed } = await Swal.fire({
      title: "เพิ่มวัตถุประสงค์",
      input: "text",
      inputPlaceholder: "กรอกวัตถุประสงค์",
      confirmButtonText: "บันทึก",
      cancelButtonText: "ยกเลิก",
      showCancelButton: true,
      buttonsStyling: true,
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
        input: "swal2-input-rounded",
      },
      willOpen: () => {
        const swalContainer = document.querySelector(".swal2-container");
        if (swalContainer) {
          swalContainer.style.zIndex = "20000";
        }
        const swalBackdrop = document.querySelector(".swal2-backdrop-show");
        if (swalBackdrop) {
          swalBackdrop.style.zIndex = "19999";
        }
      },
    });

    if (!isConfirmed) return;

    const name = String(value || "").trim();
    if (!name) {
      Swal.fire("กรอกข้อมูลไม่ครบ", "กรุณากรอกวัตถุประสงค์", "warning");
      return;
    }

    const result = await insertData(
      "Tb_Set_ServicePurpose",
      "ServicePurpose_Name, ServicePurpose_Status",
      [name, "Y"]
    );

    if (result?.status === "OK") {
      const updated = await refreshServicePurposes();
      const newRow = Array.isArray(result?.data) ? result.data[0] : null;
      const newId = newRow?.ServicePurpose_Id || newRow?.id;
      if (newId) {
        setFormData((prev) => ({
          ...prev,
          ServicePurpose: String(newId),
        }));
      } else if (updated.length > 0) {
        const latest = updated[updated.length - 1];
        setFormData((prev) => ({
          ...prev,
          ServicePurpose: String(latest.id),
        }));
      }
      Swal.fire("สำเร็จ", "เพิ่มวัตถุประสงค์แล้ว", "success");
    } else {
      Swal.fire("ผิดพลาด", result?.message || "เพิ่มวัตถุประสงค์ไม่สำเร็จ", "error");
    }
  };

  useEffect(() => {
    if (!isOpen || !ServiceAddr_Id) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/get-tb-set-service-addr-by-id/${ServiceAddr_Id}`);
        const record = res.data.result?.recordset?.[0];
        if (record) {
          const empLocal = localStorage.getItem("emp_id");
          // ถ้ามี defaultServicePurpose ให้ใช้ค่า defaultServicePurpose แทนค่า ServicePurpose จาก database
          const servicePurpose = defaultServicePurpose ? String(defaultServicePurpose) : record.ServicePurpose;
          setFormData({ 
            ...init, 
            ...record, 
            ServicePurpose: servicePurpose, // ใช้ defaultServicePurpose ถ้ามี
            Sale_Emp_Id: record.Sale_Emp_Id || empLocal || "" 
          });
          setInitialServicePurpose(servicePurpose);
          locationRef.current = servicePurpose;
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [ServiceAddr_Id, isOpen, defaultServicePurpose]);

  // useEffect สำหรับ reset formData เมื่อเปิด modal ใหม่
  useEffect(() => {
    if (!isOpen) {
      // Reset formData เมื่อปิด modal
      setFormData(init);
      locationRef.current = null;
      setInitialServicePurpose(null);
      return;
    }

    // Reset formData เมื่อเปิด modal ใหม่ (เฉพาะเมื่อไม่มี ServiceAddr_Id)
    if (!ServiceAddr_Id) {
      // ใช้ defaultServicePurpose เป็น priority สูงสุด
      const servicePurpose = defaultServicePurpose
        ? String(defaultServicePurpose)
        : (location?.ServicePurpose ? String(location.ServicePurpose) : "");
      
      // เก็บ ServicePurpose เริ่มต้น
      setInitialServicePurpose(servicePurpose);
      
      // สร้าง location object โดยไม่รวม ServicePurpose จาก location prop (ถ้ามี) เพื่อให้ใช้ค่าจาก defaultServicePurpose หรือ servicePurpose ที่คำนวณได้
      const { ServicePurpose: _, ...locationWithoutPurpose } = location || {};
      
      // ตั้งค่า formData โดยใช้ ServicePurpose ที่คำนวณได้
      setFormData({ 
        ...init, 
        ...locationWithoutPurpose, 
        ServicePurpose: servicePurpose 
      });
      locationRef.current = servicePurpose;
    }
  }, [isOpen, ServiceAddr_Id, defaultServicePurpose, location]); // ใช้ location ทั้ง object แทน location?.ServicePurpose เพื่อให้ trigger เมื่อ location เปลี่ยน

  // auto-select "ที่อยู่บริการ" จาก master เมื่อ options โหลดเสร็จ (เฉพาะสร้างใหม่)
  useEffect(() => {
    if (!isOpen || ServiceAddr_Id || defaultServicePurpose) return;
    if (servicePurposeOptions.length === 0) return;
    const match = servicePurposeOptions.find((p) => p.name === "ที่อยู่บริการ");
    if (!match) return;
    setFormData((prev) => ({
      ...prev,
      ServicePurpose: prev.ServicePurpose || String(match.id),
    }));
  }, [servicePurposeOptions, isOpen, ServiceAddr_Id, defaultServicePurpose]);

  // useEffect แยกสำหรับ update ServicePurpose เมื่อ defaultServicePurpose เปลี่ยน (หลังจาก modal เปิดแล้ว)
  useEffect(() => {
    if (!isOpen || ServiceAddr_Id) return; // ไม่ต้องทำอะไรถ้า modal ปิดหรือมี ServiceAddr_Id

    // ถ้ามี defaultServicePurpose และยังไม่ได้ตั้งค่า ให้ update
    if (defaultServicePurpose) {
      const servicePurpose = String(defaultServicePurpose);
      // ตรวจสอบว่าค่าเปลี่ยนหรือไม่
      if (locationRef.current !== servicePurpose) {
        locationRef.current = servicePurpose;
        setInitialServicePurpose(servicePurpose);
        setFormData(prev => ({ ...prev, ServicePurpose: servicePurpose }));
      }
    }
  }, [isOpen, ServiceAddr_Id, defaultServicePurpose]); // trigger เมื่อ defaultServicePurpose เปลี่ยน


  useEffect(() => {
    if (!isOpen) return;
    api.get("/provinces").then((r) => setProvinces(r.data.data || []));
  }, [isOpen]);

  useEffect(() => {
    const load = async () => {
      if (!isOpen || !formData.Province) return;
      const d = await api.get(`/districts/${formData.Province}`);
      setDistricts(d.data.data || []);
      if (formData.District) {
        const s = await api.get(`/subdistricts/${formData.District}`);
        setSubdistricts(s.data.data || []);
      }
    };
    load();
  }, [isOpen, formData.Province, formData.District]);

  useEffect(() => {
    if (!isOpen || !formData.Comp_Id) return;
    const load = async () => {
      try {
        const res = await api.get(`/get-employees/${formData.Comp_Id}`);
        setEmployees(res.data.status === "OK" ? res.data.result : []);
      } catch {
        setEmployees([]);
      }
    };
    load();
  }, [isOpen, formData.Comp_Id]);

  useEffect(() => {
    if (!isOpen || !AR_Id) return;
    const load = async () => {
      try {
        if (ServiceAddr_Id) {
          const r = await api.get(`/get-service-contacts/${ServiceAddr_Id}`);
          setContacts(r.data.result?.recordset || []);
        } else {
          const r = await api.get(`/get-tb-set-ar-contact/${AR_Id}`);
          const list = r.data.result?.recordset || [];
          setPendingContacts(list.length > 0 ? [list[0]] : []);
        }
      } catch {
        setContacts([]);
        setPendingContacts([]);
      }
    };
    load();
  }, [isOpen, AR_Id, ServiceAddr_Id]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleProvinceChange = async (value) => {
    setFormData({ ...formData, Province: value, District: "", SubDistrict: "", Zipcode: "" });
    const d = await api.get(`/districts/${value}`);
    setDistricts(d.data.data || []);
    setSubdistricts([]);
  };

  const handleDistrictChange = async (value) => {
    setFormData({ ...formData, District: value, SubDistrict: "", Zipcode: "" });
    const s = await api.get(`/subdistricts/${value}`);
    setSubdistricts(s.data.data || []);
  };

  const handleSubdistrictChange = (value) => {
    const s = subdistricts.find((x) => String(x.SubdistrictId) === String(value));
    setFormData({
      ...formData,
      SubDistrict: value,
      Zipcode: s?.ZipCode || "",
      Latitude: s?.Latitude || formData.Latitude,
      Longitude: s?.Longitude || formData.Longitude,
    });
  };

  const handleSaveContact = async (form) => {
    if (!ServiceAddr_Id) {
      if (editingContact) {
        setPendingContacts((x) =>
          x.map((c) => (c.tempId === editingContact.tempId ? { ...c, ...form } : c))
        );
      } else {
        setPendingContacts((x) => [...x, { ...form, tempId: Date.now() }]);
      }
      setIsContactModalOpen(false);
      setEditingContact(null);
      return;
    }

    try {
      if (editingContact?.AR_ContactId || editingContact?.ServiceContact_Id) {
        await api.put(`/update-service-contact/${editingContact.AR_ContactId || editingContact.ServiceContact_Id}`, {
          ...form,
          AR_Id,
          ServiceAddr_Id,
          Comp_Id: formData.Comp_Id,
        });
      } else {
        await api.post(`/create-service-contact`, {
          ...form,
          AR_Id,
          ServiceAddr_Id,
          Comp_Id: formData.Comp_Id,
        });
      }
      const r = await api.get(`/get-service-contacts/${ServiceAddr_Id}`);
      setContacts(r.data.result?.recordset || []);
      setIsContactModalOpen(false);
      setEditingContact(null);
    } catch {
      Swal.fire("ผิดพลาด", "บันทึกข้อมูลผู้ติดต่อไม่สำเร็จ", "error");
    }
  };

  const handleDeleteContact = async (c) => {
    if (!ServiceAddr_Id && c.tempId) {
      if (pendingContacts.length <= 1) {
        Swal.fire("ต้องมีผู้ติดต่ออย่างน้อย 1 คน", "", "warning");
        return;
      }
      setPendingContacts((x) => x.filter((p) => p.tempId !== c.tempId));
      return;
    }

    const ok = await Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: `ต้องการลบผู้ติดต่อ ${c.ContactName || ""}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E13F57",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });

    if (!ok.isConfirmed) return;

    try {
      await api.delete(`/delete-service-contact/${c.ServiceContact_Id}`);
      const r = await api.get(`/get-service-contacts/${ServiceAddr_Id}`);
      setContacts(r.data.result?.recordset || []);
      // Swal.fire({
      //   icon: "success",
      //   title: "สำเร็จ!",
      //   text: "ลบข้อมูลผู้ติดต่อแล้ว",
      //   timer: 1500,
      //   showConfirmButton: false,
      // });

    } catch {
      Swal.fire("ผิดพลาด!", "ไม่สามารถลบข้อมูลผู้ติดต่อได้", "error");
    }
  };

  const buildFullAddress = (loc) => {
    return [
      loc.AddrNo ? `เลขที่ ${loc.AddrNo}` : "",
      loc.Building ? `อาคาร ${loc.Building}` : "",
      loc.Village ? `หมู่บ้าน ${loc.Village}` : "",
      loc.Moo ? `ม.${loc.Moo}` : "",
      loc.Soi ? `ซ.${loc.Soi}` : "",
      loc.Road ? `ถ.${loc.Road}` : "",
      loc.SubDistrictName ? `ต.${loc.SubDistrictName}` : "",
      loc.DistrictName ? `อ.${loc.DistrictName}` : "",
      loc.ProvinceName ? `จ.${loc.ProvinceName}` : "",
      loc.Zipcode || "",
    ]
      .filter(Boolean)
      .join(" ");
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // ---------------- 🔎 Validate ----------------
    if (
      !formData.AddrNo ||
      !formData.Province ||
      !formData.District ||
      !formData.SubDistrict
    ) {
      Swal.fire("กรุณากรอกข้อมูลให้ครบ", "", "warning");
      return;
    }

    try {
      // ---------------- 📝 Save ServiceAddr ----------------
      const res = await onSave({ ...formData, attachedFiles: selectedFiles });
      const newId = res.ServiceAddr_Id;

      // ---------------- 📂 ตรวจไฟล์ใหม่จริง ----------------
      const pendingFiles = selectedFiles.filter(
        (f) => f.isNew === true && f.rawFile instanceof File
      );

      // ---------------- ⛔ ไม่มีไฟล์ใหม่ ไม่ต้องอัปโหลด ----------------
      if (pendingFiles.length > 0 && (ServiceAddr_Id || newId)) {
        const id = ServiceAddr_Id || newId;

        const fd = new FormData();
        pendingFiles.forEach((f) => fd.append("files", f.rawFile));
        fd.append("Comp_Id", formData.Comp_Id || 1);

        // 🟢 Upload
        const up = await api.post(`/upload-service-file/${id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        // 🟢 Refresh files list after upload
        if (up.data.status === "OK") {
          const r = await api.get(`/get-service-files/${id}`);
          const db = r.data.result.recordset.map((f) => ({
            File_Id: f.File_Id,
            File_Url: decodeURI(f.FilePath),
            File_Name: f.FileName,
            type: f.FileType || "",
            size: f.FileSize || 0,
            isFromDB: true,
          }));

          setSelectedFiles(db);
        }
      }

      // ---------------- 👥 Create default contact for new ServiceAddr ----------------
      if (!ServiceAddr_Id && newId && pendingContacts.length > 0) {
        for (let p of pendingContacts) {
          await api.post(`/create-service-contact`, {
            ...p,
            AR_Id,
            ServiceAddr_Id: newId,
            Comp_Id: formData.Comp_Id,
          });
        }

        const r = await api.get(`/get-service-contacts/${newId}`);
        setContacts(r.data.result?.recordset || []);
        setPendingContacts([]);
      }

      // ---------------- ✔ Success cleanup ----------------
      onReload && onReload();
      setSelectedFiles([]);
      setFormData(init);

      return {
        status: "OK",
        ServiceAddr_Id: newId,
        ServiceAddr_Name: formData.ServiceAddr_Name,
        ServiceAddr_FullName: buildFullAddress(formData),

        // ⭐ ต้องเพิ่มสองตัวนี้
        AR_ContactName:
          (contacts[0]?.ContactName) ||
          (pendingContacts[0]?.ContactName) ||
          "",

        AR_Tel:
          (contacts[0]?.ContactTel) ||
          (pendingContacts[0]?.ContactTel) ||
          "",
      };


    } catch (err) {
      console.error("❌ Save error:", err);
      Swal.fire("เกิดข้อผิดพลาดในการบันทึก", "", "error");
      return { status: "FAILED" };
    }
  };


  const handleOpenFileModal = () => {
    if (!ServiceAddr_Id) {
      Swal.fire("กรุณาบันทึกข้อมูลสถานที่ก่อนแนบไฟล์", "", "warning");
      return;
    }
    setShowFileModal(true);
  };

  useEffect(() => {
    if (!isOpen || !formData.ServiceAddr_Id) return;

    const load = async () => {
      try {
        const r = await api.get(`/get-service-files/${formData.ServiceAddr_Id}`);
        if (r.data.status !== "OK") return;

        const dbFiles = r.data.result.recordset.map((f) => ({
          File_Id: f.File_Id,
          File_Url: decodeURI(f.FilePath),
          File_Name: f.FileName,
          type: f.FileType || "",
          size: f.FileSize || 0,
          isNew: false,
          isFromDB: true,
        }));

        setSelectedFiles(dbFiles);


      } catch (err) {
        console.error("Load service files error:", err);
      }
    };

    load();
  }, [isOpen, formData.ServiceAddr_Id]);


  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (!e.ctrlKey || !mapRef.current) return;
      const el = mapRef.current.getContainer?.();
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inside =
        e.clientX >= r.left &&
        e.clientX <= r.right &&
        e.clientY >= r.top &&
        e.clientY <= r.bottom;
      if (!inside) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      e.deltaY < 0 ? mapRef.current.zoomIn() : mapRef.current.zoomOut();
    };
    window.addEventListener("wheel", handler, { passive: false, capture: true });
    return () => window.removeEventListener("wheel", handler, { passive: false, capture: true });
  }, [isOpen]);

  const isValidLatLng = (lat, lng) =>
  lat !== null &&
  lng !== null &&
  lat !== undefined &&
  lng !== undefined &&
  !isNaN(lat) &&
  !isNaN(lng);

  const BindMapRef = ({ mapRef }) => {
    const map = useMap();
    useEffect(() => {
      mapRef.current = map;
    }, [map]);
    return null;
  };


  const handleSearchLocation = async () => {
    if (!searchText.trim()) return;

    // รองรับลิงก์
    if (isGoogleMapsUrl(searchText)) {
      const pos = await extractLatLngFromGoogleUrl(searchText);
      if (pos) {
        setFormData({
          ...formData,
          Latitude: pos.lat,
          Longitude: pos.lng,
        });
        mapRef.current?.flyTo([pos.lat, pos.lng], 18);
        return;
      }
    }

    setIsSuggestionOpen(false);
    setSuggestions([]);

    try {
      const res = await api.get(`/osm-search?q=${encodeURIComponent(searchText)}`);

      if (res.data.status !== "OK" || !res.data.data.length) {
        Swal.fire("ไม่พบสถานที่", "ลองระบุชื่อสถานที่ให้ละเอียดขึ้น", "warning");
        return;
      }

      const place = res.data.data[0];
      const lat = parseFloat(place.lat);
      const lon = parseFloat(place.lon);

      setFormData({
        ...formData,
        Latitude: lat,
        Longitude: lon,
      });

      if (mapRef.current) {
        mapRef.current.flyTo([lat, lon], 18);
      }
    } catch (err) {
      console.error("Search error:", err);
      Swal.fire("เกิดข้อผิดพลาด", "ค้นหาตำแหน่งไม่สำเร็จ", "error");
    }
  };


  const isGoogleMapsUrl = (text) => {
    return text.includes("google.com/maps") ||
          text.includes("goo.gl/maps") ||
          text.includes("maps.app.goo.gl");
  };

  const extractLatLngFromGoogleUrl = async (url) => {
    try {
      const r = await api.get("/resolve-google", {
        params: { url }
      });

      if (r.data.status === "OK") {
        const { lat, lng } = r.data;
        if (lat && lng) {
          return { lat, lng };
        }
      }
      return null;

    } catch (err) {
      console.error("Google URL parse error:", err);
      return null;
    }
  };





  let suggestTimeout = null;

  const fetchSuggestions = (text) => {
    clearTimeout(suggestTimeout);

    suggestTimeout = setTimeout(async () => {
      if (!text.trim()) {
        setSuggestions([]);
        return;
      }

      try {
        const res = await api.get(`/osm-search?q=${encodeURIComponent(text)}`);

        if (res.data.status !== "OK") {
          setSuggestions([]);
          return;
        }

        const result = res.data.data;

        setSuggestions(
          result.map((item) => ({
            display: item.display_name,
            lat: parseFloat(item.lat),
            lon: parseFloat(item.lon),
          }))
        );

        setIsSuggestionOpen(true);
      } catch (err) {
        console.error("Suggestion error:", err);
      }
    }, 400);
  };




  const handleSelectSuggestion = (item) => {
    setSearchText(item.display);
    setSuggestions([]);
    setIsSuggestionOpen(false);

    // อัปเดตตำแหน่ง
    setFormData({
      ...formData,
      Latitude: item.lat,
      Longitude: item.lon,
    });

    // ขยับกล้อง
    if (mapRef.current) {
      mapRef.current.flyTo([item.lat, item.lon], 18);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9555]">
      <style>
        {`
          .swal2-input-rounded {
            border-radius: 10px !important;
          }
        `}
      </style>
      <div className="bg-white w-full max-w-5xl rounded-lg shadow-lg relative max-h-[95vh] overflow-y-auto">
        <div
          className="flex items-center gap-2 px-1 py-3 bg-white sticky top-0 z-[1111] border-b"
        >
          <Icon icon="mdi:map-marker" className="text-[#E13F57] w-6 h-6" />

          <h3 className="font-bold text-lg text-[#E13F57]">
            {ServiceAddr_Id ? "แก้ไขสถานที่บริการ" : "เพิ่มสถานที่บริการ"}
          </h3>

          <button
            onClick={onClose}
            className="ml-auto text-gray-500 hover:text-gray-700 text-xl px-2"
          >
            ✕
          </button>
        </div>


        {loading ? (
          <div className="text-center py-10 text-gray-500 animate-pulse">กำลังโหลดข้อมูล...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="border rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-[#E13F57] mb-3 whitespace-nowrap">สถานที่บริการ</h4>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm whitespace-nowrap">ประเภทสถานที่บริการ</label>
                    <button
                      type="button"
                      className="text-xs text-[#E13F57] hover:text-[#c53247]"
                      onClick={handleAddServiceType}
                    >
                      +
                    </button>
                  </div>
                  <select
                    name="ServiceAddr_Type_Id"
                    value={formData.ServiceAddr_Type_Id}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  >
                    <option value="">เลือก</option>
                    {serviceTypeOptions.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-12 md:col-span-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm">วัตถุประสงค์</label>
                    <button
                      type="button"
                      className="text-xs text-[#E13F57] hover:text-[#c53247]"
                      onClick={handleAddServicePurpose}
                    >
                      +
                    </button>
                  </div>
                  <select
                    name="ServicePurpose"
                    value={formData.ServicePurpose || initialServicePurpose || defaultServicePurpose || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  >
                    <option value="">เลือกวัตถุประสงค์</option>
                    {servicePurposeOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <label className="block text-sm mb-1 whitespace-nowrap">ชื่อสถานที่บริการ</label>
                  <input
                    name="ServiceAddr_Name"
                    value={formData.ServiceAddr_Name || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />
                </div>

                <div className="col-span-12">
                  <label className="block text-sm mb-1">ที่อยู่เลขที่ <span className="text-red-500">*</span></label>
                  <input
                    name="AddrNo"
                    value={formData.AddrNo}
                    onChange={(e) =>
                      setFormData({ ...formData, AddrNo: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />

                </div>

                <div className="col-span-12 md:col-span-4">
                  <label className="block text-sm mb-1">จังหวัด <span className="text-red-500">*</span></label>
                  <SearchableSelect
                    value={formData.Province}
                    placeholder="ค้นหา..."
                    options={provinces.map((p) => ({ value: p.ProvinceId, label: p.NameInThai }))}
                    onChange={(v) => handleProvinceChange(v)}
                  />
                </div>

                <div className="col-span-12 md:col-span-4">
                  <label className="block text-sm mb-1">เขต/อำเภอ <span className="text-red-500">*</span></label>
                  <SearchableSelect
                    value={formData.District}
                    placeholder="ค้นหา..."
                    options={districts.map((d) => ({ value: d.DistrictId, label: d.NameInThai }))}
                    onChange={(v) => handleDistrictChange(v)}
                  />
                </div>

                <div className="col-span-12 md:col-span-4">
                  <label className="block text-sm mb-1">แขวง/ตำบล <span className="text-red-500">*</span></label>
                  <SearchableSelect
                    value={formData.SubDistrict}
                    placeholder="ค้นหา..."
                    options={subdistricts.map((s) => ({ value: s.SubdistrictId, label: s.NameInThai }))}
                    onChange={(v) => handleSubdistrictChange(v)}
                  />
                </div>

                <div className="col-span-12 md:col-span-3">
                  <label className="block text-sm mb-1">รหัสไปรษณีย์</label>
                  <input
                    name="Zipcode"
                    value={formData.Zipcode || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />
                </div>

                <div className="col-span-12 md:col-span-9">
                  <label className="block text-sm mb-1">หมายเหตุ</label>
                  <input
                    name="AddrRemark"
                    value={formData.AddrRemark || ""}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px]"
                  />
                </div>
              </div>
            </div>

            {fromCustomerPage && (
              <div className="border rounded-lg p-4 shadow-sm">
                <ContactTable
                  contacts={ServiceAddr_Id ? contacts : pendingContacts}
                  onAdd={() => {
                    setEditingContact(null);
                    setIsContactModalOpen(true);
                  }}
                  onEdit={(c) => {
                    setEditingContact(c);
                    setIsContactModalOpen(true);
                  }}
                  onDelete={handleDeleteContact}
                  showAddButton={true}
                />
              </div>
            )}

            {fromCustomerPage && (
              <div className="border rounded-lg p-4 shadow-sm">
                <h4 className="font-semibold text-[#E13F57] mb-3">แผนที่ให้บริการ</h4>

                <div className="relative w-full h-[350px] rounded overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.geolocation.getCurrentPosition(
                        (pos) => {
                          setFormData({
                            ...formData,
                            Latitude: pos.coords.latitude,
                            Longitude: pos.coords.longitude,
                          });
                        },
                        () => alert("ไม่สามารถดึงตำแหน่งได้")
                      );
                    }}
                    className="absolute top-3 right-3 z-[450] bg-white border shadow px-3 py-2 rounded text-sm"
                  >
                    📍 ตำแหน่งปัจจุบัน
                  </button>
                  
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      left: "50px",
                      zIndex: 500,
                      display: "flex",
                      gap: "6px",
                      alignItems: "flex-start",
                    }}
                  >

                    {/* ปุ่ม แผนที่ / ดาวเทียม */}
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "6px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                        display: "flex",
                        overflow: "hidden",
                        border: "1px solid #ccc",
                        height: "34px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setMapType("roadmap")}
                        style={{
                          padding: "0 12px",
                          fontSize: "14px",
                          background: mapType === "roadmap" ? "#e6e6e6" : "#fff",
                          borderRight: "1px solid #ccc",
                          fontWeight: mapType === "roadmap" ? "bold" : "normal",
                          cursor: "pointer",
                        }}
                      >
                        แผนที่
                      </button>

                      <button
                        type="button"
                        onClick={() => setMapType("hybrid")}
                        style={{
                          padding: "0 12px",
                          fontSize: "14px",
                          background: mapType === "hybrid" ? "#e6e6e6" : "#fff",
                          fontWeight: mapType === "hybrid" ? "bold" : "normal",
                          cursor: "pointer",
                        }}
                      >
                        ดาวเทียม
                      </button>
                    </div>

                    {/* 🔎 ช่องค้นหา + dropdown */}
                    <div style={{ position: "relative" }}>
                      <input
                        type="text"
                        value={searchText}
                        onChange={async (e) => {
                          const text = e.target.value;
                          setSearchText(text);

                          // ถ้าเป็นลิงก์ Google Maps → แปลงเป็น lat/lng
                          if (isGoogleMapsUrl(text)) {
                            const pos = await extractLatLngFromGoogleUrl(text);
                            if (pos) {
                              setFormData({
                                ...formData,
                                Latitude: pos.lat,
                                Longitude: pos.lng,
                              });

                              if (mapRef.current) {
                                mapRef.current.flyTo([pos.lat, pos.lng], 18);
                              }
                              return; // ⛔ ไม่ต้อง autosuggest ต่อ
                            }
                          }

                          // ถ้าไม่ใช่ link → ใช้ระบบ suggest เดิม
                          fetchSuggestions(text);
                        }}

                        placeholder="ค้นหาสถานที่..."
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearchLocation();
                          }
                        }}
                        style={{
                          width: "260px",
                          height: "34px",
                          padding: "0 12px",
                          background: "#fff",
                          borderRadius: "6px",
                          border: "1px solid #ccc",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                          fontSize: "14px",
                        }}
                      />

                      {/* 🔽 Dropdown Auto-Suggest */}
                      {isSuggestionOpen && suggestions.length > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "38px",
                            left: 0,
                            width: "260px",
                            background: "#fff",
                            border: "1px solid #ccc",
                            borderRadius: "6px",
                            maxHeight: "180px",
                            overflowY: "auto",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                            zIndex: 9999,
                          }}
                        >
                          {suggestions.map((item, idx) => (
                            <div
                              key={idx}
                              onClick={() => handleSelectSuggestion(item)}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                                fontSize: "13px",
                              }}
                              onMouseDown={(e) => e.preventDefault()} // กัน Input blur
                            >
                              {item.display}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        if (mapRef.current && isValidLatLng(formData.Latitude, formData.Longitude)) {
                          mapRef.current.flyTo(
                            [parseFloat(formData.Latitude), parseFloat(formData.Longitude)],
                            18,
                            { animate: true, duration: 0.5 }
                          );
                        }
                      }}
                      style={{
                        height: "34px",
                        padding: "0 12px",
                        background: "#fff",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                        cursor: "pointer",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Icon icon="mdi:map-marker" className="text-blue-600" />
                      ไปยังตำแหน่งปักหมุด
                    </button>
                  </div>

                  <MapContainer
                    center={
                      isValidLatLng(formData.Latitude, formData.Longitude)
                        ? [parseFloat(formData.Latitude), parseFloat(formData.Longitude)]
                        : [13.7563, 100.5018]
                    }
                    zoom={18}
                    minZoom={3}
                    maxNativeZoom={22}
                    maxZoom={22}
                    whenCreated={(m) => (mapRef.current = m)}
                    scrollWheelZoom={true}
                    style={{ width: "100%", height: "100%" }}
                  >

                    <BindMapRef mapRef={mapRef} />
                    
                    <TileLayer
                      url={
                        mapType === "hybrid"
                          ? "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                          : "https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                      }
                      maxNativeZoom={22}
                      maxZoom={22}
                      minZoom={3}
                    />


                    {/* 📌 Marker */}
                    {isValidLatLng(formData.Latitude, formData.Longitude) && (
                      <Marker
                        position={[
                          parseFloat(formData.Latitude),
                          parseFloat(formData.Longitude),
                        ]}
                      />
                    )}

                    {/* 🖱 คลิกเพื่อย้าย Marker */}
                    <ClickToMoveMarker
                      onMove={(lat, lng) =>
                        setFormData({ ...formData, Latitude: lat, Longitude: lng })
                      }
                    />

                    {/* ✈ ขยับกล้องตามพิกัด */}
                    {isValidLatLng(formData.Latitude, formData.Longitude) && (
                      <FlyToNewPosition
                        lat={parseFloat(formData.Latitude)}
                        lng={parseFloat(formData.Longitude)}
                      />
                    )}
                  </MapContainer>


                </div>
              </div>
            )}

            {fromCustomerPage && (
              <>
                <div className="border rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-[#E13F57]">สเปคการทำงาน</h4>

                    <div className="relative">
                      <button
                        type="button"
                        onClick={handleOpenFileModal}
                        className="bg-[#F17F25] hover:bg-[#d96a1f] text-white px-4 h-[42px] rounded-lg flex items-center gap-2"
                      >
                        <Icon icon="mdi:paperclip" className="w-5 h-5" />
                        แนบไฟล์
                      </button>

                      {selectedFiles.length > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold px-[6px] py-[1px] rounded-full">
                          {selectedFiles.length}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="number"
                      name="Width"
                      value={formData.Width || ""}
                      onChange={handleChange}
                      placeholder="กว้าง (เมตร)"
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    />
                    <input
                      type="number"
                      name="Length"
                      value={formData.Length || ""}
                      onChange={handleChange}
                      placeholder="ยาว (เมตร)"
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    />
                    <input
                      type="number"
                      name="Height"
                      value={formData.Height || ""}
                      onChange={handleChange}
                      placeholder="สูง (ชั้น)"
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    />
                  </div>

                  {formData?.Appoint_Id || formData?.SpecRemark && (
                    <textarea
                      name="SpecRemark"
                      value={formData.SpecRemark}
                      onChange={handleChange}
                      rows="3"
                      className="w-full mt-3 border rounded px-3 py-2 h-[100px]"
                      placeholder="ผลการนัดหมาย"
                    />
                  )}


                  <textarea
                    name="Appoint_Remark"
                    value={formData.Appoint_Remark || ""}
                    onChange={handleChange}
                    rows="3"
                    className="w-full mt-3 border rounded px-3 py-2 h-[140px]"
                    placeholder="รายละเอียดเพิ่มเติม"
                  />
                </div>

                <div className="border rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-[#E13F57] mb-3">พนักงานขาย</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select
                      name="Sale_Emp_Id"
                      value={formData.Sale_Emp_Id || ""}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2 h-[42px]"
                    >
                      <option value="">-- เลือกพนักงาน --</option>
                      {employees.map((e) => (
                        <option key={e.Emp_Id} value={e.Emp_Id}>
                          {e.Emp_Name}
                        </option>
                      ))}
                    </select>
                    <div></div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <input
                      name="Advisor_Name"
                      value={formData.Advisor_Name || ""}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2 h-[42px]"
                      placeholder="ชื่อผู้แนะนำ"
                    />

                    <input
                      name="Advisor_Tel"
                      value={formData.Advisor_Tel || ""}
                      maxLength={12}
                      onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, "");
                        if (v.length > 3 && v.length <= 6) v = v.replace(/(\d{3})(\d+)/, "$1-$2");
                        else if (v.length > 6) v = v.replace(/(\d{3})(\d{3})(\d+)/, "$1-$2-$3");
                        if (v.length > 12) v = v.slice(0, 12);
                        setFormData({ ...formData, Advisor_Tel: v });
                      }}
                      className="w-full border rounded px-3 py-2 h-[42px]"
                      placeholder="เบอร์โทร"
                    />
                  </div>
                </div>
              </>
            )}

            <div
              className="sticky bottom-0 left-0 bg-white border-t py-3 flex justify-end gap-3"
              style={{
                zIndex: 50000,           //  สูงกว่า Leaflet
                position: "sticky",
              }}
            >

              <button
                type="button"
                onClick={onClose}
                className="border border-[#E13F57] text-[#E13F57] py-2 px-6 rounded-lg"
              >
                ปิด
              </button>
              <button
                type="submit"
                className="bg-[#E13F57] text-white py-2 px-6 rounded-lg"
              >
                บันทึก
              </button>
            </div>

          </form>
        )}
      </div>

      <FileModalServiceAddr
        mode="E"
        showViewAttachModal={showFileModal}
        setShowViewAttachModal={setShowFileModal}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        ServiceAddr_Id={formData.ServiceAddr_Id}
      />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
          setEditingContact(null);
        }}
        onSave={handleSaveContact}
        contact={editingContact}
        ServiceAddr_Id={ServiceAddr_Id}
      />
    </div>
  );
}
